import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, TextField, Button, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, Alert, Divider
} from '@mui/material';
import { Add, Refresh, Sync } from '@mui/icons-material';
import axios from 'axios';

export default function AdminEmpresas() {
  const [empresas, setEmpresas] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [form, setForm] = useState({ slug: '', dominio: '' });
  const [log, setLog] = useState('');
  const [creando, setCreando] = useState(false);
  const [error, setError] = useState('');

  const cargarEmpresas = async () => {
    setLoadingList(true);
    try {
      const res = await axios.get('/api/admin/empresas');
      setEmpresas(res.data.empresas || []);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => { cargarEmpresas(); }, []);

  const handleCrear = async () => {
    if (!form.slug || !form.dominio) {
      setError('Completa slug y dominio.');
      return;
    }
    setCreando(true);
    setLog('');
    setError('');

    const token = localStorage.getItem('token') || '';
    try {
      const response = await fetch('/api/admin/empresas', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(form),
      });

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let done = false;
      while (!done) {
        const { value, done: d } = await reader.read();
        done = d;
        if (value) setLog(prev => prev + decoder.decode(value));
      }
      cargarEmpresas();
    } catch (e) {
      setError(e.message);
    } finally {
      setCreando(false);
    }
  };

  const handleSyncFrontend = async (slug) => {
    try {
      await axios.post(`/api/admin/empresas/${slug}/sync-frontend`);
      alert(`Frontend sincronizado para ${slug}`);
    } catch (e) {
      alert('Error: ' + (e.response?.data?.error || e.message));
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1000, mx: 'auto' }}>
      <Typography variant="h5" fontWeight={700} gutterBottom>
        Panel Administración — Empresas
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Formulario nueva empresa */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Nueva Empresa</Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
          <TextField
            label="Slug (identificador único)"
            size="small"
            value={form.slug}
            onChange={e => setForm(f => ({ ...f, slug: e.target.value.toLowerCase().replace(/[^a-z0-9]/g, '') }))}
            placeholder="ej: acme"
            helperText="Solo letras minúsculas y números"
            sx={{ minWidth: 220 }}
          />
          <TextField
            label="Dominio"
            size="small"
            value={form.dominio}
            onChange={e => setForm(f => ({ ...f, dominio: e.target.value.trim() }))}
            placeholder="ej: acme.insightsoftsolutions.tech"
            sx={{ minWidth: 320 }}
          />
          <Button
            variant="contained"
            startIcon={creando ? <CircularProgress size={16} color="inherit" /> : <Add />}
            onClick={handleCrear}
            disabled={creando}
            sx={{ alignSelf: 'flex-start', mt: 0.5 }}
          >
            {creando ? 'Creando...' : 'Crear Empresa'}
          </Button>
        </Box>

        {log && (
          <Box
            component="pre"
            sx={{
              bgcolor: '#1e1e1e', color: '#00ff88', p: 2, borderRadius: 1,
              fontSize: 12, overflowX: 'auto', maxHeight: 300, overflowY: 'auto',
              fontFamily: 'monospace', whiteSpace: 'pre-wrap'
            }}
          >
            {log}
          </Box>
        )}
      </Paper>

      <Divider sx={{ mb: 3 }} />

      {/* Lista de empresas */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Instancias Activas</Typography>
        <Button size="small" startIcon={<Refresh />} onClick={cargarEmpresas} disabled={loadingList}>
          Actualizar
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.100' }}>
              <TableCell><strong>Nombre PM2</strong></TableCell>
              <TableCell><strong>Estado</strong></TableCell>
              <TableCell><strong>Puerto</strong></TableCell>
              <TableCell><strong>Acciones</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {empresas.length === 0 && !loadingList && (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ color: 'text.secondary' }}>
                  No hay instancias registradas
                </TableCell>
              </TableRow>
            )}
            {empresas.map(e => (
              <TableRow key={e.nombre_pm2} hover>
                <TableCell>{e.nombre_pm2}</TableCell>
                <TableCell>
                  <Chip
                    label={e.estado}
                    color={e.estado === 'online' ? 'success' : 'error'}
                    size="small"
                  />
                </TableCell>
                <TableCell>{e.puerto}</TableCell>
                <TableCell>
                  <Button
                    size="small"
                    startIcon={<Sync />}
                    onClick={() => handleSyncFrontend(e.slug)}
                    title="Copiar el frontend actual a esta instancia"
                  >
                    Sync Frontend
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Alert severity="info" sx={{ mt: 3 }}>
        <strong>Nota:</strong> Al crear una empresa se genera automáticamente: base de datos, directorio backend,
        configuración nginx y proceso PM2. El usuario inicial es <code>admin</code> / <code>admin123</code> —
        se debe cambiar en el primer acceso.
      </Alert>
    </Box>
  );
}
